// Iter 204b — Compact Goal Timing card for the Match Details modal.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Clock } from "lucide-react";
import { API } from "../App";

const TIER_TONE = {
  "A+": "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  "A":  "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "B+": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "B":  "bg-amber-500/10 text-amber-200 border-amber-500/20",
  "C":  "bg-slate-500/15 text-slate-300 border-slate-500/30",
  "NO BET": "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

export default function GoalTimingInlineCard({ homeTeam, awayTeam, league, expectedGoals, fixtureId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const lambdaHome = Number(expectedGoals?.home || 1.4);
  const lambdaAway = Number(expectedGoals?.away || 1.2);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/goal-timing/analyze`, {
          home_team: homeTeam || "Home",
          away_team: awayTeam || "Away",
          league: league || "Unknown",
          lambda_home: lambdaHome, lambda_away: lambdaAway,
          home_style: "balanced", away_style: "balanced",
          mc_iterations: 2500,
          fixture_id: fixtureId,
        });
        if (!cancel) setData(res.data);
      } catch (e) {
        if (!cancel) setError(e?.response?.data?.detail || e.message || "Analyse failed");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [homeTeam, awayTeam, league, lambdaHome, lambdaAway, fixtureId]);

  const topMarkets = useMemo(() => {
    if (!data) return [];
    return [...(data.ht_markets || []), ...(data.sh_markets || [])]
      .sort((a, b) => (b.fair_probability ?? 0) - (a.fair_probability ?? 0))
      .slice(0, 4);
  }, [data]);

  const tone = TIER_TONE[data?.risk_tier] || TIER_TONE.C;

  return (
    <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-xl p-6"
         data-testid="modal-gt-card">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-6 h-6 text-cyan-300" />
        <h3 className="text-2xl font-bold text-white">Goal Timing</h3>
        <span className="text-[10px] uppercase tracking-wide text-cyan-200/70 ml-2">
          Quarter probabilities · Survival · HT/SH markets
        </span>
      </div>

      {error && <div className="text-rose-300 text-sm mb-2">{error}</div>}
      {loading && !data && <div className="text-cyan-200/70 text-sm" data-testid="modal-gt-loading">Computing quarter hazards…</div>}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className={`rounded-lg border p-3 ${tone}`}>
              <div className="text-[10px] uppercase tracking-wide opacity-80">Verdict</div>
              <div className="text-2xl font-bold mt-0.5">{data.risk_tier}</div>
              <div className="text-xs mt-1 opacity-90">
                {data.no_bet_reason || "Add bookmaker odds in the dashboard to score edges."}
              </div>
              <div className="text-xs mt-2">Confidence: <strong>{data.confidence_pct}%</strong></div>
            </div>

            <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Half splits</div>
              <div className="mt-1 grid grid-cols-2 text-center">
                <div>
                  <div className="text-xl font-semibold text-white">{data.first_half_expected_goals}</div>
                  <div className="text-[10px] text-slate-400">1H xG</div>
                </div>
                <div>
                  <div className="text-xl font-semibold text-white">{data.second_half_expected_goals}</div>
                  <div className="text-[10px] text-slate-400">2H xG</div>
                </div>
              </div>
              <div className="text-xs text-slate-300 mt-2">
                Most dangerous: <strong className="text-white">{data.quarter_ranking?.[0]}</strong>
              </div>
              <div className="text-xs text-slate-300">
                Highest scoring half: <strong className="text-white capitalize">{data.highest_scoring_half}</strong>
              </div>
            </div>

            <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Quarter probabilities</div>
              <div className="space-y-1">
                {Object.entries(data.quarter_probabilities).map(([q, pct]) => {
                  const dangerous = q === data.quarter_ranking?.[0];
                  return (
                    <div key={q} className="flex items-center gap-1.5 text-[11px]">
                      <span className="w-12 text-right text-slate-400">{q}</span>
                      <span className="flex-1 h-2 bg-slate-700 rounded overflow-hidden">
                        <span className={`block h-full ${dangerous ? "bg-rose-400" : "bg-cyan-400"}`}
                              style={{ width: `${Math.min(100, pct * 1.5)}%` }} />
                      </span>
                      <span className="w-10 text-right font-mono text-slate-300">{pct.toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {topMarkets.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Most-likely timing markets</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {topMarkets.map((m, i) => (
                  <div key={i} className="rounded bg-slate-900/40 px-3 py-1.5 text-xs flex justify-between items-center"
                       data-testid={`modal-gt-top-${i}`}>
                    <span className="text-slate-300">
                      <span className="font-medium">{m.market}</span> · {m.selection}
                    </span>
                    <span className="text-cyan-300 font-mono">{(m.fair_probability * 100).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
